import { keepPreviousData, useQuery } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { bonusAdminBody } from "./bonusAdminBody";
import type {
  CampaignOut,
  DynamicBonusArchivedStatus,
  DynamicBonusCampaignStatus,
} from "../../components/Promotions/DynamicBonuses/types";
import {
  DEFAULT_CAMPAIGN_SORT_BY,
  DEFAULT_CAMPAIGN_SORT_ORDER,
  isDefaultCampaignSort,
  type CampaignListSortBy,
  type CampaignListSortOrder,
} from "../../components/Promotions/DynamicBonuses/Campaigns/campaignsTableSort";

type StoredToken = { AccessToken?: string };

export type ListCampaignsPageQuery = {
  status?: DynamicBonusCampaignStatus;
  page: number;
  pageSize: number;
  includeSeries?: boolean;
  sortBy?: CampaignListSortBy;
  sortOrder?: CampaignListSortOrder;
  enabled?: boolean;
};

export type ListCampaignsPage = {
  items: CampaignOut[];
  total: number;
};

const adminClient = () => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  return createPrivateAdminPanelInstance(token?.AccessToken);
};

async function fetchCampaigns(
  status?: DynamicBonusArchivedStatus,
): Promise<CampaignOut[]> {
  const instance = adminClient();
  const response = status
    ? await instance.get(API_ROUTES.LIST_DYNAMIC_BONUS_CAMPAIGNS, {
        params: { status },
      })
    : await instance.get(API_ROUTES.LIST_DYNAMIC_BONUS_CAMPAIGNS);
  return bonusAdminBody<CampaignOut[]>(response) ?? [];
}

function unwrapPagedResponse(response: { data?: unknown }): ListCampaignsPage {
  const items = bonusAdminBody<CampaignOut[]>(response) ?? [];
  const root = response?.data;
  const total =
    root != null &&
    typeof root === "object" &&
    typeof (root as { total?: unknown }).total === "number"
      ? (root as { total: number }).total
      : items.length;
  return { items, total };
}

async function fetchCampaignsPage(
  query: ListCampaignsPageQuery,
): Promise<ListCampaignsPage> {
  const instance = adminClient();
  const params: Record<string, string | number> = {
    page: query.page,
    page_size: query.pageSize,
  };
  if (query.status) {
    params.status = query.status;
  }
  if (query.includeSeries) {
    params.include_series = "true";
  }
  const sortBy = query.sortBy ?? DEFAULT_CAMPAIGN_SORT_BY;
  const sortOrder = query.sortOrder ?? DEFAULT_CAMPAIGN_SORT_ORDER;
  if (!isDefaultCampaignSort(sortBy, sortOrder)) {
    params.sort_by = sortBy;
    params.sort_order = sortOrder;
  }
  const response = await instance.get(API_ROUTES.LIST_DYNAMIC_BONUS_CAMPAIGNS, {
    params,
  });
  return unwrapPagedResponse(response);
}

export const LIST_CAMPAIGNS_QUERY_KEY = ["LIST_DYNAMIC_BONUS_CAMPAIGNS"] as const;

export const useListCampaigns = (
  status?: DynamicBonusArchivedStatus,
  options?: { enabled?: boolean },
) => {
  return useQuery<CampaignOut[]>({
    queryKey: [...LIST_CAMPAIGNS_QUERY_KEY, status ?? "all"],
    queryFn: () => fetchCampaigns(status),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  });
};

export const useListCampaignsPage = (query: ListCampaignsPageQuery) => {
  return useQuery<ListCampaignsPage>({
    queryKey: [
      ...LIST_CAMPAIGNS_QUERY_KEY,
      "page",
      query.status ?? "all",
      query.page,
      query.pageSize,
      query.includeSeries ?? false,
      query.sortBy ?? DEFAULT_CAMPAIGN_SORT_BY,
      query.sortOrder ?? DEFAULT_CAMPAIGN_SORT_ORDER,
    ],
    queryFn: () => fetchCampaignsPage(query),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    enabled: query.enabled ?? true,
  });
};
